/*
  IT Factory Australia custom collection JavaScript
  File: assets/itf-collection.js

  Controls:
  - Filter drawer open/close
  - Sort auto-submit
  - Related collection carousel arrows
  - AJAX add to cart
  - Refresh cart drawer/cart notification integration attempt
  - Fallback success/error messages
*/

(function () {
  'use strict';

  const SELECTORS = {
    section: '[data-itf-collection-section]',
    filterOpen: '[data-itf-filter-open]',
    filterClose: '[data-itf-filter-close]',
    filterDrawer: '[data-itf-filter-drawer]',
    sortSelect: '[data-itf-sort-select]',
    relatedCarousel: '[data-itf-related-carousel]',
    relatedTrack: '[data-itf-related-track]',
    relatedPrev: '[data-itf-related-prev]',
    relatedNext: '[data-itf-related-next]',
    addToCart: '[data-itf-add-to-cart]',
    cardMessage: '[data-itf-card-message]',
    cardRating: '.itf-product-card__rating'
  };

  const CLASSES = {
    drawerOpen: 'is-open',
    bodyDrawerOpen: 'itf-filter-drawer-open',
    hidden: 'is-hidden',
    disabled: 'is-disabled',
    loading: 'is-loading',
    success: 'is-success',
    error: 'is-error'
  };

  function getShopifyRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }

    return '/';
  }

  function getRoute(path) {
    const root = getShopifyRoot();

    if (root.endsWith('/')) {
      return root + path.replace(/^\//, '');
    }

    return root + '/' + path.replace(/^\//, '');
  }

  function trapFocusFallback(drawer) {
    const focusable = drawer.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, details, [tabindex]:not([tabindex="-1"])'
    );

    if (focusable.length) {
      focusable[0].focus();
    }
  }

  function initFilterDrawers(section) {
    const openButtons = section.querySelectorAll(SELECTORS.filterOpen);
    const drawer = section.querySelector(SELECTORS.filterDrawer);

    if (!drawer || !openButtons.length) return;

    let lastTrigger = null;

    function openDrawer(button) {
      lastTrigger = button || null;

      drawer.classList.add(CLASSES.drawerOpen);
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add(CLASSES.bodyDrawerOpen);

      openButtons.forEach((openButton) => {
        openButton.setAttribute('aria-expanded', 'true');
      });

      window.setTimeout(() => {
        const closeButton = drawer.querySelector('.itf-collection__filter-drawer-close');
        if (closeButton) {
          closeButton.focus();
        } else {
          trapFocusFallback(drawer);
        }
      }, 80);
    }

    function closeDrawer() {
      drawer.classList.remove(CLASSES.drawerOpen);
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove(CLASSES.bodyDrawerOpen);

      openButtons.forEach((openButton) => {
        openButton.setAttribute('aria-expanded', 'false');
      });

      if (lastTrigger && typeof lastTrigger.focus === 'function') {
        window.setTimeout(() => {
          lastTrigger.focus();
        }, 40);
      }
    }

    openButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        openDrawer(button);
      });
    });

    drawer.addEventListener('click', (event) => {
      const closeTrigger = event.target.closest(SELECTORS.filterClose);

      if (closeTrigger) {
        event.preventDefault();
        closeDrawer();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains(CLASSES.drawerOpen)) {
        closeDrawer();
      }
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth >= 1200 && drawer.classList.contains(CLASSES.drawerOpen)) {
        closeDrawer();
      }
    });
  }

  function initSortSelects(section) {
    const sortSelects = section.querySelectorAll(SELECTORS.sortSelect);

    sortSelects.forEach((select) => {
      select.addEventListener('change', () => {
        const form = select.closest('form');

        if (form) {
          form.submit();
        }
      });
    });
  }

  function initRelatedCarousels(section) {
    const carousels = section.querySelectorAll(SELECTORS.relatedCarousel);

    carousels.forEach((carousel) => {
      const track = carousel.querySelector(SELECTORS.relatedTrack);
      const prevButton = carousel.querySelector(SELECTORS.relatedPrev);
      const nextButton = carousel.querySelector(SELECTORS.relatedNext);

      if (!track) return;

      function getScrollAmount() {
        return Math.max(track.clientWidth * 0.75, 240);
      }

      function hasOverflow() {
        return track.scrollWidth > track.clientWidth + 4;
      }

      function updateButtons() {
        if (!prevButton && !nextButton) return;

        const arrowsMode = carousel.className.includes('arrows-always')
          ? 'always'
          : carousel.className.includes('arrows-never')
            ? 'never'
            : 'auto';

        if (arrowsMode === 'never') {
          if (prevButton) prevButton.classList.add(CLASSES.hidden);
          if (nextButton) nextButton.classList.add(CLASSES.hidden);
          return;
        }

        const overflow = hasOverflow();

        if (arrowsMode === 'auto' && !overflow) {
          if (prevButton) prevButton.classList.add(CLASSES.hidden);
          if (nextButton) nextButton.classList.add(CLASSES.hidden);
          return;
        }

        if (prevButton) prevButton.classList.remove(CLASSES.hidden);
        if (nextButton) nextButton.classList.remove(CLASSES.hidden);

        const maxScrollLeft = track.scrollWidth - track.clientWidth;
        const atStart = track.scrollLeft <= 2;
        const atEnd = track.scrollLeft >= maxScrollLeft - 2;

        if (prevButton) {
          prevButton.classList.toggle(CLASSES.disabled, atStart);
          prevButton.disabled = atStart;
        }

        if (nextButton) {
          nextButton.classList.toggle(CLASSES.disabled, atEnd);
          nextButton.disabled = atEnd;
        }
      }

      if (prevButton) {
        prevButton.addEventListener('click', () => {
          track.scrollBy({
            left: -getScrollAmount(),
            behavior: 'smooth'
          });
        });
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => {
          track.scrollBy({
            left: getScrollAmount(),
            behavior: 'smooth'
          });
        });
      }

      track.addEventListener('scroll', updateButtons, { passive: true });
      window.addEventListener('resize', updateButtons);

      window.setTimeout(updateButtons, 150);
      window.setTimeout(updateButtons, 600);
    });
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function getCartNotification() {
    return document.querySelector('cart-notification');
  }

  function getCartSections() {
    const cartDrawer = getCartDrawer();
    const cartNotification = getCartNotification();

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      return cartDrawer.getSectionsToRender().map((section) => section.id);
    }

    if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
      return cartNotification.getSectionsToRender().map((section) => section.id);
    }

    return [];
  }

  function openCartDrawerFromResponse(parsedState) {
    const cartDrawer = getCartDrawer();
    const cartNotification = getCartNotification();

    if (cartDrawer && typeof cartDrawer.renderContents === 'function' && parsedState && parsedState.sections) {
      cartDrawer.classList.remove('is-empty');
      cartDrawer.renderContents(parsedState);
      return true;
    }

    if (
      cartNotification &&
      typeof cartNotification.renderContents === 'function' &&
      parsedState &&
      parsedState.sections
    ) {
      cartNotification.renderContents(parsedState);
      return true;
    }

    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return true;
    }

    return false;
  }

  async function fetchCart() {
    const response = await fetch(getRoute('/cart.js'), {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Could not fetch cart.');
    }

    return response.json();
  }

  function updateCartCountBubble(cart) {
    if (!cart || typeof cart.item_count === 'undefined') return;

    const count = cart.item_count;
    const bubbles = document.querySelectorAll('.cart-count-bubble');

    bubbles.forEach((bubble) => {
      bubble.classList.toggle('hidden', count === 0);

      const visibleCount = bubble.querySelector('span[aria-hidden="true"], span:not(.visually-hidden)');
      const hiddenCount = bubble.querySelector('.visually-hidden');

      if (visibleCount) {
        visibleCount.textContent = count;
      }

      if (hiddenCount) {
        hiddenCount.textContent = count === 1 ? '1 item' : `${count} items`;
      }
    });
  }

  function setCardMessage(button, message, type) {
    const card = button.closest('[data-itf-product-card]');
    if (!card) return;

    const messageEl = card.querySelector(SELECTORS.cardMessage);
    if (!messageEl) return;

    messageEl.textContent = message || '';
    messageEl.classList.remove(CLASSES.success, CLASSES.error);

    if (type === 'success') {
      messageEl.classList.add(CLASSES.success);
    }

    if (type === 'error') {
      messageEl.classList.add(CLASSES.error);
    }
  }

  function resetButtonAfterSuccess(button) {
    const defaultText = button.dataset.defaultText || 'Add to Cart';

    window.setTimeout(() => {
      button.textContent = defaultText;
      button.classList.remove(CLASSES.success);
      button.disabled = false;
      button.setAttribute('aria-busy', 'false');
    }, 1800);
  }

  function resetButtonAfterError(button) {
    const defaultText = button.dataset.defaultText || 'Add to Cart';

    window.setTimeout(() => {
      button.textContent = defaultText;
      button.classList.remove(CLASSES.loading);
      button.disabled = false;
      button.setAttribute('aria-busy', 'false');
    }, 900);
  }

  async function addVariantToCart(button) {
    const variantId = button.dataset.variantId;

    if (!variantId || button.disabled) return;

    const defaultText = button.dataset.defaultText || 'Add to Cart';
    const addingText = button.dataset.addingText || 'Adding...';
    const addedText = button.dataset.addedText || 'Added to cart';

    button.disabled = true;
    button.classList.add(CLASSES.loading);
    button.classList.remove(CLASSES.success);
    button.setAttribute('aria-busy', 'true');
    button.textContent = addingText;
    setCardMessage(button, '', '');

    const sections = getCartSections();

    const payload = {
      id: Number(variantId),
      quantity: 1
    };

    if (sections.length) {
      payload.sections = sections;
      payload.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(getRoute('/cart/add.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const parsedState = await response.json();

      if (!response.ok) {
        const errorMessage =
          parsedState && parsedState.description
            ? parsedState.description
            : 'This product could not be added to cart.';

        throw new Error(errorMessage);
      }

      button.classList.remove(CLASSES.loading);
      button.classList.add(CLASSES.success);
      button.textContent = addedText;

      const cartDrawerOpened = openCartDrawerFromResponse(parsedState);

      try {
        const cart = await fetchCart();
        updateCartCountBubble(cart);
      } catch (cartError) {
        // Cart count update is a bonus. The product has still been added.
      }

      if (!cartDrawerOpened) {
        setCardMessage(button, 'Added to cart.', 'success');
      }

      resetButtonAfterSuccess(button);
    } catch (error) {
      button.textContent = defaultText;
      button.classList.remove(CLASSES.loading);
      setCardMessage(button, error.message || 'Could not add to cart.', 'error');
      resetButtonAfterError(button);
    }
  }

  function initAddToCart(section) {
    const buttons = section.querySelectorAll(SELECTORS.addToCart);

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        addVariantToCart(button);
      });
    });
  }

  function cleanJudgeMeCardBadges(section) {
    const ratingAreas = section.querySelectorAll(SELECTORS.cardRating);

    ratingAreas.forEach((ratingArea) => {
      const questionBadgeSelectors = [
        '.jdgm-qa-badge',
        '.jdgm-question-badge',
        '.jdgm-prev-badge__questions',
        '[data-number-of-questions]:not(.jdgm-prev-badge)'
      ];

      ratingArea
        .querySelectorAll(questionBadgeSelectors.join(','))
        .forEach((element) => {
          element.remove();
        });

      ratingArea
        .querySelectorAll('.jdgm-prev-badge__text')
        .forEach((textElement) => {
          const originalText = (textElement.textContent || '').trim();

          if (!originalText) return;

          const questionOnlyPattern = /^(no\s+questions?|\d+\s+questions?)$/i;

          if (questionOnlyPattern.test(originalText)) {
            textElement.remove();
            return;
          }

          const cleanedText = originalText
            .replace(/\s*(\||•|,|\/|-)\s*(no\s+questions?|\d+\s+questions?)\b/gi, '')
            .replace(/\b(no\s+questions?|\d+\s+questions?)\s*(\||•|,|\/|-)\s*/gi, '')
            .replace(/\s+(no\s+questions?|\d+\s+questions?)$/gi, '')
            .trim();

          if (cleanedText !== originalText) {
            textElement.textContent = cleanedText;
          }
        });
    });
  }

  function initJudgeMeCardBadges(section) {
    cleanJudgeMeCardBadges(section);

    window.setTimeout(() => cleanJudgeMeCardBadges(section), 150);
    window.setTimeout(() => cleanJudgeMeCardBadges(section), 600);
    window.setTimeout(() => cleanJudgeMeCardBadges(section), 1500);
    window.setTimeout(() => cleanJudgeMeCardBadges(section), 3000);
  }

  function initSection(section) {
    initFilterDrawers(section);
    initSortSelects(section);
    initRelatedCarousels(section);
    initAddToCart(section);
    initJudgeMeCardBadges(section);
  }

  function init() {
    const sections = document.querySelectorAll(SELECTORS.section);

    sections.forEach((section) => {
      initSection(section);
    });
  }

  document.addEventListener('DOMContentLoaded', init);

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(SELECTORS.section);

    if (section) {
      initSection(section);
    }
  });
})();